import { useEffect, useRef, useState } from "react";
import { vpAxios, money, VAIPEDE_API, downloadPdf } from "../api";
import { toast } from "sonner";
import { Share2, Copy, Check, X, ExternalLink, RefreshCw, MapPin, Printer, Bell, BellOff } from "lucide-react";

const LS_LAST = "vp_last_aguardando_ids";

export default function CardapioPedidosPage() {
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [motivo, setMotivo] = useState({});
  const [notificar, setNotificar] = useState(() => localStorage.getItem("vp_notificar") !== "0");
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await vpAxios.get("/vaipede/pedidos?origem=cardapio_digital");
      setRows(data);
      // Notifica se chegou pedido novo aguardando
      const aguardando = data.filter(p => p.status === "aguardando");
      const prevIds = (JSON.parse(localStorage.getItem(LS_LAST) || "[]"));
      const novos = aguardando.filter(p => !prevIds.includes(p.id));
      if (novos.length && prevIds.length > 0 && notificar) {
        toast.success(`🔔 ${novos.length} novo(s) pedido(s) do cardápio!`, { duration: 6000 });
        try { audioRef.current?.play(); } catch {}
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification("🔔 Novo pedido VAI PEDE", { body: `${novos[0].cliente_nome || "Cliente"} — ${money(novos[0].total)}`, tag: "vp-pedido" });
        }
        try { document.title = `(${aguardando.length}) VAI PEDE — Novo pedido!`; } catch {}
      } else {
        try { document.title = "VAI PEDE"; } catch {}
      }
      localStorage.setItem(LS_LAST, JSON.stringify(aguardando.map(p => p.id)));
    } catch {}
  };

  useEffect(() => {
    try { setUser(JSON.parse(localStorage.getItem("vaipede_user") || "{}")); } catch {}
    load();
    const iv = setInterval(load, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [notificar]);

  const pedirNotif = async () => {
    if (typeof Notification === "undefined") { toast.error("Navegador sem suporte"); return; }
    const perm = await Notification.requestPermission();
    if (perm === "granted") { toast.success("Notificações ativadas"); setNotificar(true); localStorage.setItem("vp_notificar", "1"); }
    else toast.info("Notificações não autorizadas");
  };

  const toggleNotif = () => {
    const v = !notificar;
    setNotificar(v);
    localStorage.setItem("vp_notificar", v ? "1" : "0");
    if (v && typeof Notification !== "undefined" && Notification.permission === "default") pedirNotif();
  };

  const cardapioUrl = user?.slug ? `${window.location.origin}/vaipede/cardapio/${user.slug}` : "";

  const copiar = async () => {
    try { await navigator.clipboard.writeText(cardapioUrl); toast.success("Link copiado!"); }
    catch { toast.error("Erro ao copiar"); }
  };

  const compartilharWA = () => {
    const msg = encodeURIComponent(`🍔 *${user?.loja_nome || "Confira nosso cardápio!"}*\n\n${cardapioUrl}\n\nPeça direto pelo WhatsApp!`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const aceitar = async (p) => {
    try {
      const { data } = await vpAxios.patch(`/vaipede/pedidos/${p.id}/status`, { status: "aceito" });
      toast.success(`Pedido #${p.numero} aceito!`);
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
      // 🖨️ Impressão automática ao aceitar — abre PDF direto para o operador imprimir
      downloadPdf(`/vaipede/pedidos/${p.id}/recibo-termico`, `pedido-${p.numero}.pdf`).catch(() => {});
      load();
    } catch { toast.error("Erro"); }
  };

  const rejeitar = async (p) => {
    const m = motivo[p.id] || "";
    try {
      const { data } = await vpAxios.patch(`/vaipede/pedidos/${p.id}/status`, { status: "rejeitado", motivo: m });
      toast.info(`Pedido #${p.numero} rejeitado`);
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
      load();
    } catch { toast.error("Erro"); }
  };

  const imprimir = (id, numero) => {
    downloadPdf(`/vaipede/pedidos/${id}/recibo-termico`, `pedido-${numero || id}.pdf`)
      .catch(() => toast.error("Erro ao gerar PDF"));
  };

  const aguardando = rows.filter(p => p.status === "aguardando");
  const outros = rows.filter(p => p.status !== "aguardando");

  return (
    <div data-testid="vaipede-cardapio-pedidos">
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU..." preload="auto"/>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black">Pedidos do Cardápio Digital</h1>
          <p className="text-sm text-neutral-500">Chegam aqui os pedidos do link público</p>
        </div>
        <div className="flex gap-2">
          <button onClick={toggleNotif} className={`p-2.5 rounded-xl border ${notificar ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-neutral-200 text-neutral-500"}`} data-testid="vaipede-toggle-notif" title="Notificações">
            {notificar ? <Bell className="w-4 h-4"/> : <BellOff className="w-4 h-4"/>}
          </button>
          <button onClick={load} className="p-2.5 rounded-xl bg-white border border-neutral-200"><RefreshCw className="w-4 h-4"/></button>
        </div>
      </div>

      {/* Link + Compartilhar */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 mb-6">
        <h2 className="font-bold mb-3">🔗 Link do seu cardápio</h2>
        {cardapioUrl ? (
          <>
            <div className="bg-neutral-50 rounded-xl border border-neutral-200 px-4 py-3 mb-3 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-neutral-400 shrink-0"/>
              <a href={cardapioUrl} target="_blank" rel="noreferrer" className="text-sm truncate text-[#EA1D2C] hover:underline flex-1">{cardapioUrl}</a>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={compartilharWA} className="px-4 py-2.5 rounded-xl bg-[#25D366] text-white font-bold text-sm flex items-center gap-2 hover:brightness-110" data-testid="vaipede-share-wa">
                <Share2 className="w-4 h-4"/> Compartilhar no WhatsApp
              </button>
              <button onClick={copiar} className="px-4 py-2.5 rounded-xl bg-neutral-100 font-bold text-sm flex items-center gap-2" data-testid="vaipede-copy-link">
                <Copy className="w-4 h-4"/> Copiar link
              </button>
              <a href={cardapioUrl} target="_blank" rel="noreferrer" className="px-4 py-2.5 rounded-xl bg-neutral-900 text-white font-bold text-sm flex items-center gap-2">
                <ExternalLink className="w-4 h-4"/> Abrir cardápio
              </a>
            </div>
          </>
        ) : <p className="text-sm text-neutral-500">Complete o cadastro da loja para gerar seu link.</p>}
      </div>

      <h2 className="font-black text-lg mb-3">📥 Aguardando aceite ({aguardando.length})</h2>
      {aguardando.length === 0 && <p className="text-sm text-neutral-500 mb-6">Nenhum pedido aguardando.</p>}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        {aguardando.map(p => (
          <div key={p.id} className="bg-white rounded-2xl border-2 border-amber-300 p-4 relative">
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-black rounded-full min-w-[24px] h-6 grid place-items-center animate-pulse">NEW</span>
            <div className="flex items-center justify-between mb-2">
              <div className="font-black text-lg">#{p.numero}</div>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold">AGUARDANDO</span>
            </div>
            <div className="text-sm mb-2">
              <b>{p.cliente_nome || "Cliente"}</b>{p.cliente_whatsapp ? ` · ${p.cliente_whatsapp}` : ""}
            </div>
            {p.endereco && <div className="text-xs text-neutral-600 mb-2">📍 {p.endereco}</div>}
            {p.referencia && <div className="text-xs text-neutral-600 mb-2">🧭 <b>Ref:</b> {p.referencia}</div>}
            {(p.latitude && p.longitude) && (
              <a href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 inline-flex items-center gap-1 mb-2">
                <MapPin className="w-3 h-3"/> Ver no mapa
              </a>
            )}
            <div className="space-y-1 text-sm mb-2 bg-neutral-50 rounded-lg p-2">
              {(p.itens || []).map((it, i) => <div key={i}><b>{it.quantidade}x</b> {it.nome} — {money(it.preco)}</div>)}
            </div>
            {p.observacao && <div className="text-xs bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-2">📝 {p.observacao}</div>}
            {p.forma_pagamento && <div className="text-xs mb-2">💳 {p.forma_pagamento === "infinitepay" ? "✅ Pago via InfinitePay" : p.forma_pagamento}</div>}
            {p.forma_pagamento === "dinheiro" && p.troco_para && Number(p.troco_para) > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-2 mb-2 text-xs" data-testid={`vaipede-troco-${p.id}`}>
                <div className="font-black text-amber-800">💰 TROCO PARA {money(p.troco_para)}</div>
                <div className="text-amber-700 font-bold">→ Levar {money(Math.max(0, Number(p.troco_para) - Number(p.total || 0)))} de troco</div>
              </div>
            )}
            <div className="flex items-center justify-between mb-3 text-lg"><b>Total</b><b className="text-[#EA1D2C]">{money(p.total)}</b></div>
            <input value={motivo[p.id] || ""} onChange={e => setMotivo({...motivo, [p.id]: e.target.value})} placeholder="Motivo (se rejeitar)" className="w-full px-3 py-2 rounded-xl border border-neutral-200 text-xs mb-2"/>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => aceitar(p)} className="py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-1" data-testid={`vaipede-aceitar-${p.id}`}>
                <Check className="w-4 h-4"/> Aceitar
              </button>
              <button onClick={() => rejeitar(p)} className="py-2.5 rounded-xl bg-red-600 text-white font-bold text-sm flex items-center justify-center gap-1" data-testid={`vaipede-rejeitar-${p.id}`}>
                <X className="w-4 h-4"/> Rejeitar
              </button>
              <button onClick={() => imprimir(p.id, p.numero)} className="py-2.5 rounded-xl bg-neutral-100 font-bold text-sm flex items-center justify-center gap-1">
                <Printer className="w-4 h-4"/>
              </button>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-black text-lg mb-3">Histórico ({outros.length})</h2>
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        {outros.length === 0 && <p className="text-sm text-neutral-500 text-center py-6">Sem histórico.</p>}
        <div className="divide-y divide-neutral-100">
          {outros.map(p => (
            <div key={p.id} className="p-3 flex items-center justify-between text-sm">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <b>#{p.numero}</b> · {p.cliente_nome}
                  {p.status === "finalizado" && (
                    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[10px] font-black px-2 py-0.5 rounded-full border border-emerald-300" data-testid={`vaipede-hist-selo-${p.id}`}>
                      ✓ QUITADO
                    </span>
                  )}
                </div>
                <div className="text-xs text-neutral-500">{new Date(p.created_at).toLocaleString("pt-BR")}</div>
              </div>
              <div className="text-right mr-3">
                <div className="font-black text-[#EA1D2C]">{money(p.total)}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "rejeitado" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{p.status}</span>
              </div>
              <button onClick={() => imprimir(p.id, p.numero)} className="p-2 rounded-lg bg-neutral-100" data-testid={`vaipede-hist-imp-${p.id}`}>
                <Printer className="w-4 h-4"/>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
